import { useEffect, useRef } from 'react';

export default function RainOverlay({ active }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!active) return undefined;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
        const canvas = ref.current;
        const ctx = canvas.getContext('2d');
        const DPR = Math.min(window.devicePixelRatio || 1, 2);
        let W, H, raf;

        const resize = () => {
            W = canvas.width = window.innerWidth * DPR;
            H = canvas.height = window.innerHeight * DPR;
        };
        resize();
        window.addEventListener('resize', resize);

        const spawn = (anywhere) => {
            const depth = 0.35 + Math.random() * 0.65; // far drops: slower, thinner, fainter
            return {
                x: Math.random() * (W + 120 * DPR) - 60 * DPR,
                y: anywhere ? Math.random() * H : -40 * DPR - Math.random() * H * 0.15,
                len: (10 + Math.random() * 18) * depth * DPR,
                speed: (9 + Math.random() * 10) * depth * DPR,
                drift: (0.9 + Math.random() * 1.3) * depth * DPR,
                alpha: 0.10 + depth * 0.28,
                width: Math.max(0.6, depth * 1.4) * DPR,
            };
        };
        const count = Math.min(170, Math.max(60, Math.floor(window.innerWidth / 9)));
        const drops = Array.from({ length: count }, () => spawn(true));

        ctx.lineCap = 'round';
        const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const strokeColor = dark ? '#93aacb' : '#5b7191';
        const tick = () => {
            ctx.clearRect(0, 0, W, H);
            ctx.strokeStyle = strokeColor;
            for (const d of drops) {
                ctx.globalAlpha = d.alpha;
                ctx.lineWidth = d.width;
                ctx.beginPath();
                ctx.moveTo(d.x, d.y);
                ctx.lineTo(d.x - d.drift * (d.len / d.speed), d.y - d.len);
                ctx.stroke();
                d.x += d.drift;
                d.y += d.speed;
                if (d.y - d.len > H || d.x - d.len > W + 80 * DPR) Object.assign(d, spawn(false));
            }
            raf = requestAnimationFrame(tick);
        };
        tick();
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
        };
    }, [active]);

    if (!active) return null;
    return (
        <canvas
            ref={ref}
            aria-hidden="true"
            data-testid="rain-canvas"
            style={{
                position: 'fixed', inset: 0, width: '100vw', height: '100vh',
                pointerEvents: 'none', zIndex: 5,
                animation: 'hydlib-fade-in 1.4s ease both',
            }}
        />
    );
}
